import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShoppingBag, Check } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import CustomFieldsForm from "./CustomFieldsForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProdutoFormDialog({ open, onClose, editItem = null, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ nome: "", descricao: "", custom_fields: {} });
  const [customFields, setCustomFields] = useState([]);
  const [formLayout, setFormLayout] = useState([]);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#8B5CF6";

  const fetchData = useCallback(async () => {
    try {
      const [cfRes, layoutRes] = await Promise.all([
        axios.get(`${API}/custom-fields?module=produtos`, { withCredentials: true }),
        axios.get(`${API}/form-layout/produtos`, { withCredentials: true }),
      ]);
      setCustomFields(cfRes.data);
      setFormLayout(layoutRes.data.fields || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (open) {
      fetchData();
      setForm(editItem
        ? { nome: editItem.nome || "", descricao: editItem.descricao || "", custom_fields: editItem.custom_fields || {} }
        : { nome: "", descricao: "", custom_fields: {} });
    }
  }, [open, editItem, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let res;
      if (editItem) res = await axios.put(`${API}/produtos/${editItem.id}`, form, { withCredentials: true });
      else res = await axios.post(`${API}/produtos`, form, { withCredentials: true });
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) { console.error(err); }
  };

  const orderedLayout = formLayout.length > 0
    ? [...formLayout].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [
        { field_name: "nome", fixed: true, field_width: "100" },
        { field_name: "descricao", fixed: true, field_width: "100" },
        ...customFields.map(cf => ({ field_name: cf.field_name, fixed: false, field_width: "100" })),
      ];

  const fixedRenderers = {
    nome: () => <div key="nome"><Label className="text-slate-700">Nome</Label><Input value={form.nome} onChange={e => setForm(p => ({...p, nome: e.target.value}))} required /></div>,
    descricao: () => <div key="descricao"><Label className="text-slate-700">Descrição</Label><Input value={form.descricao || ""} onChange={e => setForm(p => ({...p, descricao: e.target.value}))} /></div>,
  };

  const renderItem = (item) => {
    if (item.fixed && fixedRenderers[item.field_name]) return fixedRenderers[item.field_name]();
    if (!item.fixed) { const cf = customFields.find(c => c.field_name === item.field_name); if (cf) return <CustomFieldsForm key={cf.field_name} fields={[cf]} values={form.custom_fields} onChange={cfv => setForm(p => ({...p, custom_fields: cfv}))} layout={[item]} />; }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" style={{ color: tc500 }} />
            {editItem ? "Editar" : "Novo"} Produto
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {orderedLayout.map((item, i) => {
              const rendered = renderItem(item);
              if (!rendered) return null;
              return <div key={i} className={item.field_width === "50" ? "col-span-1" : "col-span-2"}>{rendered}</div>;
            })}
          </div>
          <Button type="submit" style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90">
            <Check className="w-4 h-4 mr-2" />{t("common.save")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
